import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Badge } from '@/shared/components/ui/badge'
import { Loader2, Users as UsersIcon, LayoutGrid, Building2, Info } from 'lucide-react'
import type { User, UserCreateInput, UserUpdateInput } from '@/modules/users/types/user'
import { useAllGroupExtensions } from '@/modules/users/hooks/useGroups'
import { useAllAreas } from '@/modules/users/hooks/useAreas'
import * as areasApi from '@/modules/users/api/areasApi'
import { TransferList } from './TransferList'

// Schema for creating users - all required fields
const userCreateSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
  first_name: z.string().min(1, 'El nombre es requerido'),
  last_name: z.string().min(1, 'El apellido es requerido'),
  informacion: z.string().min(1, 'La información es requerida'),
  telefono: z.string().optional(),
  is_staff: z.boolean().optional(),
  is_active: z.boolean().optional(),
})

// Schema for editing users - all fields optional
const userEditSchema = z.object({
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres').optional().or(z.literal('')),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  informacion: z.string().optional(),
  telefono: z.string().optional(),
  is_staff: z.boolean().optional(),
  is_active: z.boolean().optional(),
})

type UserFormData = z.infer<typeof userCreateSchema> | z.infer<typeof userEditSchema>

interface UserFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: any) => Promise<any> | void
  isSubmitting: boolean
  user?: User | null
  mode: 'create' | 'edit'
}

export function UserFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  user,
  mode,
}: UserFormDialogProps) {
  const [assignedGroupIds, setAssignedGroupIds] = useState<number[]>([])
  const [assignedAreaIds, setAssignedAreaIds] = useState<number[]>([])
  const [initialAreaIds, setInitialAreaIds] = useState<number[]>([])


  // Fetch all available groups and areas
  const { data: groupExtensions } = useAllGroupExtensions()
  const { data: allAreas, isLoading: isLoadingAreas } = useAllAreas()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<UserFormData>({
    resolver: zodResolver(mode === 'create' ? userCreateSchema : userEditSchema),
    defaultValues: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      informacion: '',
      telefono: '',
      is_staff: false,
      is_active: true,
    },
  })

  const isStaff = watch('is_staff')
  const isActive = watch('is_active')

  // Reset form when user changes or dialog opens/closes
  useEffect(() => {
    if (open && mode === 'edit' && user && groupExtensions) {
      reset({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        informacion: user.informacion,
        telefono: user.telefono || '',
        is_staff: user.is_staff,
        is_active: user.is_active,
        password: '', // Don't populate password on edit
      })

      // Map user's group names to Django group IDs
      const userGroupIds = groupExtensions
        .filter((ext) => user.groups.includes(ext.grupo_nombre))
        .map((ext) => ext.grupo_id)

      setAssignedGroupIds(userGroupIds)

      // Load user areas
      import('@/modules/users/api/usersApi').then(({ getUserAreas }) => {
        getUserAreas(user.id).then((areas) => {
          const areaIds = areas.map((a) => a.id)
          setAssignedAreaIds(areaIds)
          setInitialAreaIds(areaIds)
        }).catch(() => {
          setAssignedAreaIds([])
          setInitialAreaIds([])
        })
      })
    } else if (open && mode === 'create') {
      reset({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        informacion: '',
        telefono: '',
        is_staff: false,
        is_active: true,
      })
      setAssignedGroupIds([])
      setAssignedAreaIds([])
      setInitialAreaIds([])
    } else if (!open) {
      setAssignedGroupIds([])
      setAssignedAreaIds([])
      setInitialAreaIds([])
    }
  }, [open, mode, user, reset, groupExtensions])

  const syncAreaAssignments = async (userId: number) => {
    // Determine which areas to add and remove
    const toAdd = assignedAreaIds.filter((id) => !initialAreaIds.includes(id))
    const toRemove = initialAreaIds.filter((id) => !assignedAreaIds.includes(id))

    const promises: Promise<any>[] = []
    for (const areaId of toAdd) {
      promises.push(areasApi.assignUserToArea(areaId, userId))
    }
    for (const areaId of toRemove) {
      promises.push(areasApi.removeUserFromArea(areaId, userId))
    }

    if (promises.length > 0) {
      await Promise.all(promises)
    }
  }

  const handleFormSubmit = async (data: UserFormData) => {
    if (mode === 'edit') {
      // For edit, only send fields that have values (PATCH request)
      // No enviar groups: los signals del backend los gestionan desde las áreas
      const updateData: Partial<UserUpdateInput> = {}

      // Only include fields that are not empty
      if (data.email && data.email.trim()) {
        updateData.email = data.email.trim()
      }
      if (data.first_name && data.first_name.trim()) {
        updateData.first_name = data.first_name.trim()
      }
      if (data.last_name && data.last_name.trim()) {
        updateData.last_name = data.last_name.trim()
      }
      if (data.informacion && data.informacion.trim()) {
        updateData.informacion = data.informacion.trim()
      }
      if (data.telefono && data.telefono.trim()) {
        updateData.telefono = data.telefono.trim()
      } else if (data.telefono === '') {
        updateData.telefono = null
      }
      if (data.password && data.password.length >= 8) {
        updateData.password = data.password
      }
      if (data.is_staff !== undefined) {
        updateData.is_staff = data.is_staff
      }
      if (data.is_active !== undefined) {
        updateData.is_active = data.is_active
      }

      // Sincronizar áreas primero (los signals asignan grupos automáticamente)
      if (user) {
        await syncAreaAssignments(user.id)
      }

      onSubmit(updateData)
    } else {
      // Para crear, primero crear el usuario sin groups
      // Luego sincronizar áreas (los signals asignan grupos automáticamente)
      const createData: UserCreateInput = {
        email: data.email!,
        password: data.password!,
        first_name: data.first_name!,
        last_name: data.last_name!,
        informacion: data.informacion!,
        telefono: data.telefono || null,
        is_staff: data.is_staff ?? false,
        is_active: data.is_active ?? true,
      }
      const createdUser = await onSubmit(createData)

      // Sincronizar áreas después de crear el usuario
      if (createdUser?.id && assignedAreaIds.length > 0) {
        await syncAreaAssignments(createdUser.id)
      }
    }
  }

  const handleAreaTransfer = (itemIds: number[], toAssigned: boolean) => {
    if (toAssigned) {
      setAssignedAreaIds((prev) => [...prev, ...itemIds])
    } else {
      setAssignedAreaIds((prev) => prev.filter((id) => !itemIds.includes(id)))
    }
  }

  // Preview de laboratorios y grupos derivados de las áreas seleccionadas
  const selectedAreas = (allAreas || []).filter((a) => assignedAreaIds.includes(a.id))
  const previewLabs = [...new Set(
    selectedAreas
      .filter((a) => a.laboratorio_nombre)
      .map((a) => a.laboratorio_nombre!)
  )]

  // Grupos actuales del usuario (para modo edit, read-only)
  const regularGroups = groupExtensions?.filter((groupExt) => !groupExt.laboratorio)
  const labGroups = groupExtensions?.filter((groupExt) => groupExt.laboratorio)
  const assignedGroupNames = regularGroups
    ?.filter((ext) => assignedGroupIds.includes(ext.grupo_id))
    .map((ext) => ext.grupo_nombre) || []
  const assignedLabNames = labGroups
    ?.filter((ext) => assignedGroupIds.includes(ext.grupo_id))
    .map((ext) => ext.grupo_nombre) || []

  // Map areas to TransferItem
  const activeAreas = allAreas?.filter((a) => a.is_active) || []
  const assignedAreaItems = activeAreas
    .filter((a) => assignedAreaIds.includes(a.id))
    .map((a) => ({ id: a.id, label: a.nombre, subtitle: a.laboratorio_nombre || undefined }))
  const unassignedAreaItems = activeAreas
    .filter((a) => !assignedAreaIds.includes(a.id))
    .map((a) => ({ id: a.id, label: a.nombre, subtitle: a.laboratorio_nombre || undefined }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Crear Usuario' : 'Editar Usuario'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Complete los datos del nuevo usuario'
              : 'Actualice la información del usuario'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="first_name">
                Nombre {mode === 'create' && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="first_name"
                {...register('first_name')}
                placeholder="Juan"
                disabled={isSubmitting}
              />
              {errors.first_name && (
                <p className="text-sm text-destructive">{errors.first_name.message}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="last_name">
                Apellido {mode === 'create' && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="last_name"
                {...register('last_name')}
                placeholder="Pérez"
                disabled={isSubmitting}
              />
              {errors.last_name && (
                <p className="text-sm text-destructive">{errors.last_name.message}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email {mode === 'create' && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              placeholder="usuario@example.com"
              disabled={isSubmitting}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">
              Contraseña{' '}
              {mode === 'create' && <span className="text-destructive">*</span>}
              {mode === 'edit' && (
                <span className="text-sm text-muted-foreground">(Dejar en blanco para no cambiar)</span>
              )}
            </Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              placeholder="••••••••"
              disabled={isSubmitting}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          {/* Informacion */}
          <div className="space-y-2">
            <Label htmlFor="informacion">
              Información {mode === 'create' && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="informacion"
              {...register('informacion')}
              placeholder="Descripción o cargo del usuario"
              disabled={isSubmitting}
            />
            {errors.informacion && (
              <p className="text-sm text-destructive">{errors.informacion.message}</p>
            )}
          </div>

          {/* Telefono */}
          <div className="space-y-2">
            <Label htmlFor="telefono">Teléfono</Label>
            <Input
              id="telefono"
              {...register('telefono')}
              placeholder="+593999999999"
              disabled={isSubmitting}
            />
          </div>

          {/* Switches */}
          <div className="space-y-4 rounded-lg border p-4">
            <h4 className="text-sm font-medium">Permisos y Estado</h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is_active">Usuario Activo</Label>
                <p className="text-sm text-muted-foreground">
                  El usuario puede iniciar sesión en la plataforma
                </p>
              </div>
              <Switch
                id="is_active"
                checked={isActive}
                onCheckedChange={(checked) => setValue('is_active', checked)}
                disabled={isSubmitting}
                className="data-[state=checked]:bg-teal-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is_staff">Acceso de Staff</Label>
                <p className="text-sm text-muted-foreground">
                  El usuario tiene acceso al panel de administración
                </p>
              </div>
              <Switch
                id="is_staff"
                checked={isStaff}
                onCheckedChange={(checked) => setValue('is_staff', checked)}
                disabled={isSubmitting}
                className="data-[state=checked]:bg-teal-500"
              />
            </div>
          </div>

          {/* Areas Selection - único control editable */}
          {isLoadingAreas ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <TransferList
              title="Áreas"
              icon={LayoutGrid}
              description="Al asignar áreas, el usuario hereda automáticamente los laboratorios y grupos configurados"
              availableItems={unassignedAreaItems}
              assignedItems={assignedAreaItems}
              onTransfer={handleAreaTransfer}
              disabled={isSubmitting}
            />
          )}

          {/* Laboratorios - read-only, derivados de las áreas */}
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-medium">Laboratorios</h4>
            </div>
            {mode === 'create' ? (
              // En modo crear, mostrar preview basado en áreas seleccionadas
              previewLabs.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {previewLabs.map((lab) => (
                    <Badge key={lab} variant="secondary" className="text-xs">
                      {lab}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Se asignarán automáticamente según las áreas seleccionadas
                </p>
              )
            ) : (
              // En modo editar, mostrar los labs actuales del usuario
              assignedLabNames.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {assignedLabNames.map((lab) => (
                    <Badge key={lab} variant="secondary" className="text-xs">
                      {lab}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin laboratorios asignados</p>
              )
            )}
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              Gestionados automáticamente desde las áreas
            </p>
          </div>

          {/* Grupos - read-only, derivados de las áreas */}
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-medium">Grupos</h4>
            </div>
            {mode === 'edit' && assignedGroupNames.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {assignedGroupNames.map((group) => (
                  <Badge key={group} variant="secondary" className="text-xs">
                    {group}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Se asignarán automáticamente según las áreas seleccionadas
              </p>
            )}
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              Gestionados automáticamente desde las áreas
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-teal-800 hover:bg-teal-900 text-white">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
